import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Chip } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { useListTagsQuery } from '~redux/slices/apiSlice'
import { type IdentityId, type TagData, type UserId } from '~types'
import { loadResourcePermittedOrgIdentitiesWithAdHoc } from '~utils/permissionsUtils'

import Dropdown, { type DropdownOption } from './Dropdown'

interface TagDropdownProps {
  selectedTags: TagData[]
  onChange: (selectedTags: TagData[]) => void
  selectedIdentityId?: string
  label?: string
  placeholder?: string
  startAdornment?: React.ReactNode
  showSelected?: boolean
  fullWidth?: boolean
  renderTrigger?: () => React.ReactNode
}

const TagDropdown: React.FC<TagDropdownProps> = ({
  selectedTags,
  onChange,
  selectedIdentityId,
  label,
  placeholder = 'Select tags...',
  startAdornment,
  showSelected = true,
  fullWidth = true,
  renderTrigger,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const [tagPermittedOrgIdentities, setTagPermittedOrgIdentities] = useState<IdentityId[]>([])

  useEffect(() => {
    const loadPermissions = async (): Promise<void> => {
      if (!userId) return
      const policies = await loadResourcePermittedOrgIdentitiesWithAdHoc(userId as UserId, [
        'cases',
        'processes',
        'contacts',
      ])
      setTagPermittedOrgIdentities(policies.read)
    }
    loadPermissions()
  }, [userId])

  const ownerIds = selectedIdentityId ? [selectedIdentityId] : tagPermittedOrgIdentities

  const { data: listTagsResponse } = useListTagsQuery(
    {
      user_id: userId as string,
      owner_ids: ownerIds,
    },
    { skip: !userId || ownerIds.length === 0 },
  )

  const allTags = listTagsResponse?.tags ?? []

  const tagMap = useMemo(() => {
    const map = new Map<string, TagData>()
    for (const tag of allTags) {
      map.set(tag.id, tag)
    }
    return map
  }, [allTags])

  const options: DropdownOption[] = useMemo(
    () =>
      allTags
        .filter((tag) =>
          showSelected ? true : !selectedTags.some((selectedTag) => selectedTag.id === tag.id),
        )
        .map((tag) => ({
          value: tag.id,
          label: tag.name,
          chip: <Chip label={tag.name} color={tag.color} size='small' />,
        })),
    [allTags, selectedTags, showSelected],
  )

  const selectedValues = useMemo(
    () => (showSelected ? selectedTags.map((t) => t.id) : []),
    [selectedTags, showSelected],
  )

  const handleChange = (value: string | string[]): void => {
    const ids = Array.isArray(value) ? value : [value]
    const newTags = ids
      .map((id) => tagMap.get(id) ?? selectedTags.find((tag) => tag.id === id))
      .filter(Boolean) as TagData[]

    if (showSelected) {
      onChange(newTags)
    } else {
      onChange([...selectedTags, ...newTags])
    }
  }

  return (
    <Dropdown
      options={options}
      value={selectedValues}
      onChange={handleChange}
      multiple
      searchable
      label={label}
      placeholder={placeholder}
      startAdornment={startAdornment}
      fullWidth={fullWidth}
      renderTrigger={renderTrigger}
    />
  )
}

export default TagDropdown
